import type {
  Either,
  ApiSuccess,
  ApiFailure,
  BusinessError,
  GenericError,
  ApiError,
  Left,
  Right,
} from './either.type';
import { TOO_MANY_REQUESTS_MESSAGE } from '@/shared/common/errors/common.error';
import { logger } from '@/shared/common/utils/logger.util';
import { captureApiErrorTrace } from '@/core/sentry';
import { AxiosError } from 'axios';

/**
 * Convierte una respuesta exitosa de Axios a ApiSuccess
 * @param response - Respuesta exitosa de Axios
 * @returns ApiSuccess con los datos de la respuesta
 */
export const handleSuccessResponse = <T>(response: any): ApiSuccess<T> => {
  const success: ApiSuccess<T> = {
    data: response.data,
    status: response.status,
  };

  return success;
};

/**
 * Convierte un error de Axios a un Either con BusinessError o GenericError
 * @param error - Error de Axios
 * @returns Either con BusinessError o GenericError
 */
export const handleErrorResponse = (error: any): Either<ApiFailure, never> => {
  const err = error as ApiError;
  if (!err?.response) {
    const genericError: GenericError = {
      code: error.code || 'NETWORK_ERROR',
      reason: 'GENERIC_ERROR',
      status: 0,
      message: getErrorMessage(error),
    };
    logger.apiError(0, error.config?.url || '', error, { message: error.message });
    return left(genericError);
  }

  const { code, reason, message } = err.response?.data || {};
  const status = err.response?.status || 500;

  // Si tenemos un reason, es un error de negocio
  if (reason) {
    const businessError: BusinessError = {
      code: code || 'UNKNOWN',
      reason,
      status,
      message: message || getErrorMessage(error),
    };

    logger.apiError(status, error.config?.url || '', error, {
      message: err.message,
      reason,
      code,
    });

    return left(businessError);
  }

  // Si no hay reason, es un error genérico
  const genericError: GenericError = {
    code: error.code || 'UNKNOWN',
    reason: 'GENERIC_ERROR',
    status,
    message: getErrorMessage(error),
  };

  logger.apiError(status, error.config?.url || '', error, {
    message: error.message,
  });

  return left(genericError);
};

/**
 * Obtiene un mensaje de error legible basado en el tipo de error
 * @param error - Error de Axios
 * @returns Mensaje de error legible
 */
const getErrorMessage = (error: any): string => {
  if (error.response) {
    const status = error.response.status;
    switch (status) {
      case 400:
        return 'Solicitud incorrecta';
      case 401:
        return 'No autorizado';
      case 403:
        return 'Acceso prohibido';
      case 404:
        return 'Recurso no encontrado';
      case 409:
        return 'Conflicto de datos';
      case 422:
        return 'Datos de validación incorrectos';
      case 429:
        return TOO_MANY_REQUESTS_MESSAGE;
      case 500:
        return 'Error interno del servidor';
      case 502:
        return 'Error de gateway';
      case 503:
        return 'Servicio no disponible';
      default:
        return `Error del servidor: ${status}`;
    }
  } else if (error.request) {
    return 'Error de conexión. Verifica tu conexión a internet';
  } else {
    return 'Error de configuración de la solicitud';
  }
};

/**
 * Ejecuta una función asíncrona y maneja automáticamente los errores con Either
 * @param requestFn - Función que retorna una promesa de Axios
 * @returns Promise con Either
 */
export const executeRequest = async <T>(
  requestFn: () => Promise<any>
): Promise<Either<ApiFailure, ApiSuccess<T>>> => {
  try {
    const response = await requestFn();
    const success = handleSuccessResponse<T>(response);
    return right(success);
  } catch (error) {
    if (error && typeof error === 'object' && 'isAxiosError' in error) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        captureApiErrorTrace(error as any);
      }
      return handleErrorResponse(error);
    }

    // Error no esperado
    const unexpectedError: GenericError = {
      code: 'UNEXPECTED_ERROR',
      reason: 'UNEXPECTED_ERROR',
      status: 500,
      message: 'Error inesperado',
    };

    logger.error('Unexpected Error', { error }, error as Error);
    return left(unexpectedError);
  }
};

/**
 * Función helper para manejar múltiples Either en secuencia
 * @param eithers - Array de Either
 * @returns Either con array de datos exitosos o primer error encontrado
 */
export const sequence = <T>(
  eithers: Either<ApiFailure, ApiSuccess<T>>[]
): Either<ApiFailure, ApiSuccess<T[]>> => {
  const results: T[] = [];

  for (const either of eithers) {
    if (either.isLeft()) {
      return left(either.value);
    }
    results.push(either.value.data);
  }

  return right({
    data: results,
    status: 200,
  });
};

/**
 * Función helper para transformar datos de un Either
 * @param either - Either original
 * @param transform - Función de transformación
 * @returns Either con datos transformados
 */
export const mapEither = <T, U>(
  either: Either<ApiFailure, ApiSuccess<T>>,
  transform: (data: T) => U
): Either<ApiFailure, ApiSuccess<U>> => {
  return either.map((success) => ({
    ...success,
    data: transform(success.data),
  }));
};

/**
 * Implementación funcional de Left
 */
const createLeft = <L, R>(value: L): Left<L, R> => ({
  _tag: 'Left',
  value,
  isLeft: function (this: Left<L, R>): this is Left<L, R> {
    return true;
  },
  isRight: function (this: Left<L, R>): this is Right<L, R> {
    return false;
  },
  map: () => createLeft<L, any>(value) as any,
  flatMap: () => createLeft<L, any>(value) as any,
  fold: (onLeft, _onRight) => onLeft(value),
  foldAsync: async (onLeft, _onRight) => await onLeft(value),
});

/**
 * Implementación funcional de Right
 */
const createRight = <L, R>(value: R): Right<L, R> => ({
  _tag: 'Right',
  value,
  isLeft: function (this: Right<L, R>): this is Left<L, R> {
    return false;
  },
  isRight: function (this: Right<L, R>): this is Right<L, R> {
    return true;
  },
  map: (f) => createRight<L, any>(f(value)),
  flatMap: (f) => f(value),
  fold: (_onLeft, onRight) => onRight(value),
  foldAsync: async (_onLeft, onRight) => await onRight(value),
});

/**
 * Funciones helper para crear Either
 */
export const left = <L, R = never>(value: L): Left<L, R> =>
  createLeft<L, R>(value);
export const right = <R, L = never>(value: R): Right<L, R> =>
  createRight<L, R>(value);
